import { message } from 'antd';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useRestApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { AggregatedTaskPieChart } from 'business/shift-report/report/pages/ReportAggregated/components/aggregated-task-pie-chart';
import { useReportControllerV1GetTopLevelCriticalTaskAggregate } from 'generated/apiComponents';
import { GetShiftReportPdfMetadataQuery, Module_Enum } from 'generated/graphql';
import { useMediaType } from 'technical/media/hooks';
import Loader from 'ui/loader';

interface Props {
  report: NonNullable<GetShiftReportPdfMetadataQuery['shiftReport']>;
}

export function ShiftProductionGraph({ report }: Props) {
  const { currentConstructionSiteId } = useAppContext();
  invariant(currentConstructionSiteId, 'No constructionSite id');

  const { t } = useTranslation();
  const { isMobile } = useMediaType();

  const restHeaders = useRestApiHeaders(Module_Enum.ShiftReport);

  const { data, isLoading, error } =
    useReportControllerV1GetTopLevelCriticalTaskAggregate({
      queryParams: {
        constructionSiteId: currentConstructionSiteId,
        startDate: report.date,
        endDate: report.date,
        reportId: report.id,
      },
      headers: restHeaders,
    });

  if (error) {
    message.error(t('errors.error_generic'));
    return (
      <div style={{ textAlign: 'center' }}>{t('errors.no_data_available')}</div>
    );
  }

  const graphData = data?.firstLevelAggregation.filter(
    (task) => task.criticalTotal,
  );

  return (
    <>
      {isLoading && <Loader />}
      {graphData && !isLoading && (
        <AggregatedTaskPieChart
          aggregatedTasks={graphData}
          criticalTime={true}
          isMobile={isMobile}
        />
      )}
    </>
  );
}
